/**
 * Pure view-model helpers for the ChatHeader identity badges / breadcrumb.
 *
 * Keeps the component presentation a thin projection over a single
 * {@link buildChatHeaderIdentity} call so the badge matrix
 * ({kind, workerRole, parentThreadId}) can be unit-tested without mounting.
 */

import type { ThreadId, WorkerRole } from "@cmmd-forge/contracts";

export interface ChatHeaderIdentityInput {
  readonly kind: "master" | "worker" | null | undefined;
  readonly workerRole?: WorkerRole | null;
  readonly parentThreadId?: ThreadId | null;
  readonly parentTitle?: string | null;
  readonly workerCount?: number;
}

export interface ChatHeaderIdentityVM {
  /**
   * Primary identity badge. Master/lead threads intentionally have no role
   * badge in the header; the header chip is reserved for project context.
   */
  readonly badge: {
    readonly kind: "worker";
    readonly label: "WORKER";
    readonly workerRole: WorkerRole | null;
  } | null;
  /**
   * Worker breadcrumb back to the master thread. Populated only on worker
   * threads that know their `parentThreadId`.
   */
  readonly parentBreadcrumb: {
    readonly parentThreadId: ThreadId;
    readonly label: string;
  } | null;
}

/**
 * Compute the ChatHeader identity view-model.
 *
 * - master → no role badge; project context is rendered by the component.
 * - worker (with `parentThreadId`) → `{ badge: { kind: "worker", workerRole },
 *   parentBreadcrumb: { parentThreadId, label } }`.
 * - worker (orphan) → breadcrumb is `null`, badge still renders.
 * - neither / unknown → `{ badge: null, parentBreadcrumb: null }` (no decoration).
 */
export function buildChatHeaderIdentity(input: ChatHeaderIdentityInput): ChatHeaderIdentityVM {
  const { kind, workerRole, parentThreadId, parentTitle } = input;

  if (kind === "master") {
    return {
      badge: null,
      parentBreadcrumb: null,
    };
  }

  if (kind === "worker") {
    const parentBreadcrumb =
      parentThreadId !== null && parentThreadId !== undefined
        ? {
            parentThreadId,
            label: parentTitle && parentTitle.length > 0 ? parentTitle : "Master",
          }
        : null;
    return {
      badge: {
        kind: "worker",
        label: "WORKER",
        workerRole: workerRole ?? null,
      },
      parentBreadcrumb,
    };
  }

  return { badge: null, parentBreadcrumb: null };
}
